/**
 * Collection task builder — ARCHITECTURE.md section 5.4.5.
 *
 * Deterministically constructs scoped collection tasks from the scope
 * contract and desk config. The LLM does NOT decide what to search —
 * the orchestrator constructs specific queries from the decomposed
 * parameters and the desk config sources.
 *
 * Pure deterministic code — no LLM calls.
 */

import type {
  DeskConfig,
  DeskSource,
} from '@/config/desk-config';
import type {
  CollectionTask,
  CollectionPlan,
  ScopeContract,
  InvestigationTierConfig,
} from './types';
import { getRelevantEnrichments, inferContexts } from './enrichment-registry';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function uuid(): string {
  return crypto.randomUUID();
}

// Average tokens per source query (rough estimate for budget planning)
const ESTIMATED_TOKENS_PER_TASK = 5000;

// ---------------------------------------------------------------------------
// Source filtering by tier
// ---------------------------------------------------------------------------

/**
 * Filter desk sources based on tier's source filter policy.
 * - top_3_by_reliability: Only the 3 highest-reliability enabled sources
 * - all_enabled: All enabled sources
 * - all_including_low_reliability: All sources including disabled ones with D/E/F reliability
 */
function filterSourcesByTier(
  sources: DeskSource[],
  sourceFilter: InvestigationTierConfig['sourceFilter'],
): DeskSource[] {
  const reliabilityOrder: Record<string, number> = {
    A: 6, B: 5, C: 4, D: 3, E: 2, F: 1,
  };

  switch (sourceFilter) {
    case 'top_3_by_reliability': {
      const enabled = sources.filter(s => s.enabled);
      enabled.sort((a, b) =>
        (reliabilityOrder[b.reliability] ?? 0) - (reliabilityOrder[a.reliability] ?? 0),
      );
      return enabled.slice(0, 3);
    }
    case 'all_enabled':
      return sources.filter(s => s.enabled);
    case 'all_including_low_reliability':
      return [...sources];
    default:
      return sources.filter(s => s.enabled);
  }
}

// ---------------------------------------------------------------------------
// Keyword extraction from scope contract
// ---------------------------------------------------------------------------

/**
 * Build search keywords from the scope contract.
 * Deterministic: pulls from subject description, behavior indicators, exclusions.
 */
function buildKeywords(scope: ScopeContract): string[] {
  const keywords: string[] = [];

  // From subject
  if (scope.subject.description) {
    // Extract significant words (3+ chars, no stop words)
    const stopWords = new Set([
      'the', 'and', 'for', 'with', 'from', 'that', 'this', 'are', 'was',
      'were', 'has', 'have', 'had', 'been', 'not', 'but', 'its', 'using',
    ]);
    const words = scope.subject.description
      .replace(/[^a-zA-Z0-9\s-]/g, '')
      .split(/\s+/)
      .filter(w => w.length >= 3 && !stopWords.has(w.toLowerCase()));
    keywords.push(...words);
  }

  // From subject subtype
  if (scope.subject.subtype) {
    keywords.push(scope.subject.subtype);
  }

  // From behavior indicators
  for (const indicator of scope.behaviorIndicators) {
    // Extract meaningful keywords from indicator type and threshold
    const typeWords = indicator.type.replace(/_/g, ' ').split(/\s+/);
    keywords.push(...typeWords);
  }

  // From watchlist matches
  for (const match of scope.subject.watchlistMatches) {
    keywords.push(match);
  }

  // Deduplicate (case-insensitive)
  const seen = new Set<string>();
  return keywords.filter(k => {
    const lower = k.toLowerCase();
    if (seen.has(lower)) return false;
    seen.add(lower);
    return true;
  });
}

// ---------------------------------------------------------------------------
// Task construction per source type
// ---------------------------------------------------------------------------

function buildTaskForRSSSource(
  investigationId: string,
  source: DeskSource,
  scope: ScopeContract,
  keywords: string[],
): CollectionTask {
  return {
    id: `task_${uuid()}`,
    investigationId,
    sourceId: source.id,
    query: {
      type: 'rss',
      url: source.url ?? '',
      keywords: keywords.slice(0, 10), // limit keywords
      temporalBounds: {
        start: scope.temporalBounds.start,
        end: scope.temporalBounds.end,
      },
    },
    expectedEntityTypes: getExpectedEntityTypes(scope),
    status: 'pending',
    rawData: null,
    startedAt: null,
    completedAt: null,
    error: null,
  };
}

function buildTaskForAPISource(
  investigationId: string,
  source: DeskSource,
  scope: ScopeContract,
  keywords: string[],
): CollectionTask {
  const query: Record<string, unknown> = {
    type: 'api',
    endpoint: source.endpoint ?? '',
    keywords,
    geographicBounds: {
      bbox: scope.geographicBounds.bbox,
    },
    temporalBounds: {
      start: scope.temporalBounds.start,
      end: scope.temporalBounds.end,
    },
  };

  // For maritime APIs: add indicator-specific query parameters
  if (source.name.toLowerCase().includes('marine') || source.name.toLowerCase().includes('ais')) {
    for (const indicator of scope.behaviorIndicators) {
      if (indicator.type === 'ais_dark') {
        query.aisDark = true;
      }
      if (indicator.type === 'flag_change') {
        query.flagChange = true;
      }
    }
  }

  return {
    id: `task_${uuid()}`,
    investigationId,
    sourceId: source.id,
    query,
    expectedEntityTypes: getExpectedEntityTypes(scope),
    status: 'pending',
    rawData: null,
    startedAt: null,
    completedAt: null,
    error: null,
  };
}

function buildTaskForTelegramSource(
  investigationId: string,
  source: DeskSource,
  scope: ScopeContract,
  keywords: string[],
): CollectionTask {
  return {
    id: `task_${uuid()}`,
    investigationId,
    sourceId: source.id,
    query: {
      type: 'telegram',
      channelId: source.channelId ?? '',
      keywords: keywords.slice(0, 10),
      temporalBounds: {
        start: scope.temporalBounds.start,
        end: scope.temporalBounds.end,
      },
    },
    expectedEntityTypes: getExpectedEntityTypes(scope),
    status: 'pending',
    rawData: null,
    startedAt: null,
    completedAt: null,
    error: null,
  };
}

function buildTaskForGenericSource(
  investigationId: string,
  source: DeskSource,
  scope: ScopeContract,
  keywords: string[],
): CollectionTask {
  return {
    id: `task_${uuid()}`,
    investigationId,
    sourceId: source.id,
    query: {
      type: source.type,
      keywords,
      geographicBounds: {
        bbox: scope.geographicBounds.bbox,
      },
      temporalBounds: {
        start: scope.temporalBounds.start,
        end: scope.temporalBounds.end,
      },
    },
    expectedEntityTypes: getExpectedEntityTypes(scope),
    status: 'pending',
    rawData: null,
    startedAt: null,
    completedAt: null,
    error: null,
  };
}

// ---------------------------------------------------------------------------
// Entity type inference from scope
// ---------------------------------------------------------------------------

/**
 * Infer expected entity types from the scope contract's subject type.
 * Always includes SOURCE. Adds related types based on subject.
 */
function getExpectedEntityTypes(scope: ScopeContract): string[] {
  const types = new Set<string>();

  // Always include the subject type and SOURCE
  types.add(scope.subject.type);
  types.add('SOURCE');

  // Common associations
  switch (scope.subject.type) {
    case 'ASSET':
      types.add('LOCATION');
      types.add('ORGANIZATION');
      types.add('INDICATOR');
      types.add('EVENT');
      break;
    case 'PERSON':
      types.add('ORGANIZATION');
      types.add('LOCATION');
      types.add('EVENT');
      break;
    case 'ORGANIZATION':
      types.add('PERSON');
      types.add('LOCATION');
      types.add('ASSET');
      break;
    case 'EVENT':
      types.add('LOCATION');
      types.add('PERSON');
      types.add('ORGANIZATION');
      types.add('ASSET');
      break;
    case 'INDICATOR':
      types.add('ASSET');
      types.add('LOCATION');
      types.add('EVENT');
      break;
    case 'LOCATION':
      types.add('ASSET');
      types.add('ORGANIZATION');
      types.add('EVENT');
      break;
  }

  return Array.from(types);
}

// ---------------------------------------------------------------------------
// GDELT-specific task (special case — free, no key needed)
// ---------------------------------------------------------------------------

/**
 * Build a GDELT-specific collection task.
 * GDELT is always available as a free source; this creates
 * a keyword + geographic query against the GDELT API.
 */
function buildGDELTTask(
  investigationId: string,
  scope: ScopeContract,
  keywords: string[],
): CollectionTask {
  return {
    id: `task_${uuid()}`,
    investigationId,
    sourceId: 'gdelt',
    query: {
      type: 'gdelt',
      keywords: keywords.slice(0, 15),
      geographicBounds: {
        bbox: scope.geographicBounds.bbox,
      },
      temporalBounds: {
        start: scope.temporalBounds.start,
        end: scope.temporalBounds.end,
      },
      mode: 'artlist',
      maxRecords: 50,
    },
    expectedEntityTypes: getExpectedEntityTypes(scope),
    status: 'pending',
    rawData: null,
    startedAt: null,
    completedAt: null,
    error: null,
  };
}

// ---------------------------------------------------------------------------
// Enrichment task building
// ---------------------------------------------------------------------------

/**
 * Check which enrichment API keys are available.
 * Checks localStorage (Settings panel) and environment variables.
 */
function getAvailableEnrichmentKeys(): Set<string> {
  const keys = new Set<string>();
  const keyMap: Record<string, string> = {
    shodan: 'VITE_SHODAN_API_KEY',
    virustotal: 'VITE_VIRUSTOTAL_API_KEY',
  };

  for (const [sourceId, envKey] of Object.entries(keyMap)) {
    try {
      const stored = localStorage.getItem(`sentinel-key-${sourceId}`);
      if (stored) { keys.add(sourceId); continue; }
    } catch { /* localStorage unavailable */ }
    if (import.meta.env?.[envKey]) keys.add(sourceId);
  }

  // Free sources are always available
  keys.add('bgpview');
  keys.add('whois');
  keys.add('dns');

  return keys;
}

/**
 * Build enrichment tasks based on investigation context.
 * Only adds enrichment sources that are relevant to the investigation's
 * subject type, keywords, and available API keys.
 */
function buildEnrichmentTasks(
  investigationId: string,
  scope: ScopeContract,
  keywords: string[],
): CollectionTask[] {
  const contexts = inferContexts(scope.subject.type, keywords);
  if (contexts.length === 0) return [];

  const entityTypes = getExpectedEntityTypes(scope);
  const availableKeys = getAvailableEnrichmentKeys();
  const enrichments = getRelevantEnrichments(entityTypes, contexts, availableKeys);

  return enrichments.map(mapping => ({
    id: `task_${uuid()}`,
    investigationId,
    sourceId: mapping.sourceId,
    query: {
      type: mapping.sourceId,
      keywords,
      geographicBounds: {
        bbox: scope.geographicBounds.bbox,
      },
      temporalBounds: {
        start: scope.temporalBounds.start,
        end: scope.temporalBounds.end,
      },
    },
    expectedEntityTypes: entityTypes,
    status: 'pending' as const,
    rawData: null,
    startedAt: null,
    completedAt: null,
    error: null,
  }));
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Build a collection plan from the scope contract and desk config.
 * Deterministic — the LLM does NOT decide what to search.
 * The orchestrator constructs specific queries from the decomposed
 * parameters and the desk config.
 */
export function buildCollectionPlan(
  investigationId: string,
  scope: ScopeContract,
  deskConfig: DeskConfig,
  tierConfig: InvestigationTierConfig,
): CollectionPlan {
  const keywords = buildKeywords(scope);
  const sources = filterSourcesByTier(deskConfig.sources, tierConfig.sourceFilter);
  const tasks: CollectionTask[] = [];

  // Check if any desk source is GDELT; if not, add it as a built-in free source
  // But only if the tier allows it — QUICK_SCAN (top_3_by_reliability) should
  // only use desk-configured sources, not inject extras
  const hasGDELT = sources.some(s =>
    s.id.toLowerCase().includes('gdelt') || s.name.toLowerCase().includes('gdelt'),
  );

  if (!hasGDELT && tierConfig.sourceFilter !== 'top_3_by_reliability') {
    tasks.push(buildGDELTTask(investigationId, scope, keywords));
  }

  // Build tasks per desk source
  for (const source of sources) {
    let task: CollectionTask;

    switch (source.type) {
      case 'rss':
        task = buildTaskForRSSSource(investigationId, source, scope, keywords);
        break;
      case 'api':
        task = buildTaskForAPISource(investigationId, source, scope, keywords);
        break;
      case 'telegram_channel':
        task = buildTaskForTelegramSource(investigationId, source, scope, keywords);
        break;
      default:
        task = buildTaskForGenericSource(investigationId, source, scope, keywords);
        break;
    }

    tasks.push(task);
  }

  // Layer 1: Curated web sources (when desk config has webSources)
  if (deskConfig.webSources && deskConfig.webSources.length > 0) {
    tasks.push({
      id: `task_${crypto.randomUUID()}`,
      investigationId,
      sourceId: 'curated_web',
      query: {
        type: 'curated_web',
        keywords,
        subject: scope.subject.description,
        region: scope.geographicBounds?.source,
        webSources: deskConfig.webSources,
      },
      expectedEntityTypes: [],
      status: 'pending',
      rawData: null,
      startedAt: null,
      completedAt: null,
      error: null,
    });
  }

  // Layer 2: SearXNG web search (STANDARD and DEEP tiers only)
  if (tierConfig.sourceFilter !== 'top_3_by_reliability') {
    const searchConfig = deskConfig.searchConfig ?? {};
    const maxResults = tierConfig.sourceFilter === 'all_including_low_reliability'
      ? (searchConfig.maxResults?.deep ?? 20)
      : (searchConfig.maxResults?.standard ?? 10);

    tasks.push({
      id: `task_${crypto.randomUUID()}`,
      investigationId,
      sourceId: 'web_search',
      query: {
        type: 'web_search',
        keywords,
        subject: scope.subject.description,
        region: scope.geographicBounds?.source,
        searxngUrl: searchConfig.searxngUrl,
        maxResults,
      },
      expectedEntityTypes: [],
      status: 'pending',
      rawData: null,
      startedAt: null,
      completedAt: null,
      error: null,
    });
  }

  // Add enrichment tasks if the investigation context warrants them
  const enrichmentTasks = buildEnrichmentTasks(investigationId, scope, keywords);
  tasks.push(...enrichmentTasks);

  return {
    investigationId,
    tasks,
    totalSources: tasks.length,
    estimatedTokens: tasks.length * ESTIMATED_TOKENS_PER_TASK,
  };
}
